'use client';

import { Logo, LogoText } from '@shared/ui';
import { PROJECT_NAME } from '@shared/constants';
import { cn } from '@shared/utils';

/**
 * @group Brand
 * @summary Brand lockup component combining Phila icon and wordmark
 * 
 * Renders the Phila brand as a consistent icon + wordmark combination.
 * Supports two size variants: 'default' for headers and 'compact' for footers.
 * 
 * @example
 * ```tsx
 * <PhilaBrandLockup variant="default" />
 * <PhilaBrandLockup variant="compact" className="text-brand" />
 * <PhilaBrandLockup color="#084349" logoColor="#084349" textColor="#084349" />
 * ```
 */
interface PhilaBrandLockupProps {
  /** Size variant for the lockup */
  variant?: 'default' | 'compact';
  /** Additional CSS classes for the container */
  className?: string;
  /** Color for both logo and text (SVG fill color) */
  color?: string;
  /** Color specifically for the logo icon (SVG fill color) */
  logoColor?: string;
  /** Color specifically for the text wordmark (SVG fill color) */
  textColor?: string;
  /** Additional CSS classes for the logo icon */
  logoClassName?: string;
  /** Additional CSS classes for the text wordmark */
  textClassName?: string;
  /** Size for the logo and text */
  size?: number;
}

export function PhilaBrandLockup({ 
  variant = 'default',
  className,
  color,
  logoColor,
  textColor,
  logoClassName,
  textClassName,
  size = 48
}: PhilaBrandLockupProps) {
  const iconSize = variant === 'compact' ? 32 : size;

  // LogoText should be slightly smaller than the icon for better visual balance
  const textSize = Math.round(size * 0.75);
  
  // Use specific colors if provided, otherwise fall back to the general color prop
  const finalLogoColor = logoColor ?? color;
  const finalTextColor = textColor ?? color;
  
  return (
    <div 
      className={cn('flex items-center gap-1', className)}
      aria-label={`${PROJECT_NAME} home`}
    >
      <Logo 
        size={size} 
        color={finalLogoColor}
        className={cn("flex-shrink-0", logoClassName)}
      />
      <LogoText 
        size={textSize}
        color={finalTextColor}
        className={cn("flex-shrink-0", textClassName)}
      />
    </div>
  );
}
